import React, { useEffect, useState } from 'react';
import ProgressBarComponent from 'src/components/common/ProgressBarComponent';
import { archLevel } from 'src/data/config';
import { setProgress } from 'src/store/Loader';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import { updateUserAccess } from 'src/util/util';
import { Gantt, Task } from '../../GanttChart';
import { useMilestoneTracking } from './useMilestoneTracking';

const MilestoneGanttChart = ({ viewModeType, currentMonth, currentYear, currentMonthDate }: any) => {
  const { selectedAppSection } = useSelectedAppInfo();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const dispatch = useAppDispatch();
  const [timeEvents, setTimeEvents] = useState({ year: currentYear, month: currentMonth });
  const { milestoneGanttData, milestoneGanttDataError, getMilestoneGanttLazyData } = useMilestoneTracking();
  const makeTaskObj = (minorMilestones: any) => {
    let taskList: Task[] = [];
    minorMilestones.forEach((row: any) => {
      if (
        row.is_activated &&
        row.min_start_date != null &&
        row.min_start_date !== '' &&
        row.max_end_date != null &&
        row.max_end_date !== '' &&
        row.min_start_date <= row.max_end_date
      ) {
        if (
          (viewModeType == 'Month' &&
            currentYear >= new Date(row.min_start_date).getFullYear() &&
            currentYear <= new Date(row.max_end_date).getFullYear()) ||
          viewModeType == 'All' ||
          (currentYear >= new Date(row.min_start_date).getFullYear() &&
            currentYear <= new Date(row.max_end_date).getFullYear() &&
            viewModeType == 'Day' &&
            (currentMonth == new Date(row.min_start_date).getMonth() + 1 ||
              currentMonth == new Date(row.max_end_date).getMonth() + 1)) ||
          (Date.parse(currentMonthDate) <= Date.parse(row.max_end_date) &&
            Date.parse(currentMonthDate) >= Date.parse(row.min_start_date))
        ) {
          const t: Task = {
            start: new Date(row.min_start_date),
            end: new Date(row.max_end_date),
            name: row.minor_milestone_name,
            id: row.milestone_id,
            progress: Math.round(row.progress),
            duration: row.duration,
            type: 'task',
            project: row.milestone_reference,
            styles: { progressColor: '#ffbb54', progressSelectedColor: '#ff9e0d' }
          };
          taskList = [...taskList, t];
        }
      }
    });
    return taskList;
  };

  const [tasks, setTasks] = React.useState<Task[]>([]);
  const columnWidth = 70;

  const getUpdateMilestoneData = (milestoneGanttData: any) => {
    let taskList: Task[] = [];
    milestoneGanttData.forEach((milestone: any) => {
      if (
        milestone.is_activated &&
        milestone.min_start_date != null &&
        milestone.min_start_date !== '' &&
        milestone.max_end_date != null &&
        milestone.max_end_date !== '' &&
        milestone.min_start_date <= milestone.max_end_date
      ) {
        if (
          (viewModeType == 'Month' &&
            currentYear >= new Date(milestone.min_start_date).getFullYear() &&
            currentYear <= new Date(milestone.max_end_date).getFullYear()) ||
          viewModeType == 'All' ||
          (currentYear >= new Date(milestone.min_start_date).getFullYear() &&
            currentYear <= new Date(milestone.max_end_date).getFullYear() &&
            viewModeType == 'Day' &&
            (currentMonth == new Date(milestone.min_start_date).getMonth() + 1 ||
              currentMonth == new Date(milestone.max_end_date).getMonth() + 1)) ||
          (viewModeType !== 'Month' &&
            Date.parse(currentMonthDate) <= Date.parse(milestone.max_end_date) &&
            Date.parse(currentMonthDate) >= Date.parse(milestone.min_start_date))
        ) {
          const t: Task = {
            start: new Date(milestone.min_start_date),
            end: new Date(milestone.max_end_date),
            name: milestone.major_milestone_name,
            id: milestone.milestone_reference,
            progress: Math.round(milestone.progress),
            duration: milestone.duration,
            type: 'project',
            hideChildren: false,
            styles: { progressColor: '#ffbb54', progressSelectedColor: '#ff9e0d' }
          };
          const minorData = makeTaskObj(milestone.minor_milestones);
          if (minorData.length > 0 && viewModeType == 'Day') {
            taskList = [...taskList, t];
          } else if (viewModeType !== 'Day') {
            taskList = [...taskList, t];
          }
          taskList = [...taskList, ...minorData];
          setTasks(taskList);
        } else {
          setTasks(taskList);
        }
      } else {
        setTasks(taskList);
      }
      dispatch(setProgress(false));
    });
  };

  useEffect(() => {
    if (milestoneGanttDataError) {
      updateUserAccess(milestoneGanttData, dispatch);
    }
  }, [milestoneGanttData]);

  useEffect(() => {
    if (milestoneGanttData) {
      getUpdateMilestoneData(milestoneGanttData);
    }
    setTimeEvents({ year: currentYear, month: currentMonth });
  }, [viewModeType, currentMonth, currentYear, milestoneGanttData]);

  useEffect(() => {
    getMilestoneGanttLazyData({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: archLevel === 'site' ? process.env.REACT_APP_TSDA_SLUG : process.env.REACT_APP_NAME,
      application_section: selectedAppSection
    });
  }, []);
  const handleExpanderClick = (task: Task) => {
    setTasks(tasks.map(t => (t.id === task.id ? task : t)));
  };

  return (
    <div className="gantt-view">
      <ProgressBarComponent />
      {tasks.length > 0 ? (
        <Gantt
          timeEvents={timeEvents}
          tasks={tasks}
          viewMode={viewModeType === 'All' ? 'Month' : viewModeType}
          clonedviewMode={viewModeType}
          onExpanderClick={handleExpanderClick}
          columnWidth={columnWidth}
        />
      ) : (
        <div className="gantt-view-text">
          <h4>No Data Available</h4>
        </div>
      )}
    </div>
  );
};

export default MilestoneGanttChart;
